<!--
  文件描述：多业态装修模板列表页，支持从各业态模块入口带 industry 参数进入
  @author DIY
  @createTime 2026-07-22
-->
<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :model="queryParams"
      :inline="true"
      label-width="72px"
      class="template-query-form"
    >
      <el-form-item label="模板名称" prop="name">
        <el-input v-model="queryParams.name" class="!w-200px" clearable placeholder="请输入模板名称" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="业态" prop="industry">
        <el-select
          v-model="queryParams.industry"
          class="!w-180px"
          :clearable="!industrySelectDisabled"
          :disabled="industrySelectDisabled"
          placeholder="请选择业态"
        >
          <!-- 业态筛选选项：显示图标和名称 -->
          <el-option v-for="item in industryList" :key="item.industry" :label="item.name" :value="item.industry">
            <span class="industry-option">
              <Icon :icon="getIndustryIcon(item.industry, item.icon)" />
              <span>{{ item.name }}</span>
            </span>
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="请选择状态">
          <!-- 状态筛选选项 -->
          <el-option v-for="item in TEMPLATE_STATUS_OPTIONS" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="场景" prop="scene">
        <el-select v-model="queryParams.scene" class="!w-160px" clearable placeholder="请选择场景">
          <!-- 场景筛选选项 -->
          <el-option v-for="item in DEFAULT_SCENE_OPTIONS" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="应用状态" prop="applied">
        <el-select v-model="queryParams.applied" class="!w-160px" clearable placeholder="请选择应用状态">
          <el-option label="当前应用" :value="true" />
          <el-option label="未应用" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="handleResetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
        <el-button type="primary" plain @click="handleCreate">
          <Icon icon="ep:plus" class="mr-5px" />新建模板
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-skeleton :loading="loading" animated :count="CARD_SKELETON_COUNT">
      <template #template>
        <el-row :gutter="CARD_GUTTER">
          <el-col v-for="item in CARD_SKELETON_COUNT" :key="item" :span="CARD_COL_SPAN">
            <el-skeleton-item variant="image" class="template-skeleton" />
          </el-col>
        </el-row>
      </template>
      <template #default>
        <el-alert
          v-if="queryParams.applied !== false && list.length > 0 && !hasAppliedTemplate"
          class="template-state-alert"
          type="warning"
          show-icon
          :closable="false"
          title="当前列表没有应用中的模板，请确认后端 applied/isApplied 字段或先应用一个已发布模板"
        />
        <el-empty v-if="list.length === 0" description="暂无模板" />
        <div v-else class="template-grid">
          <!-- 模板卡片列表 -->
          <div
            v-for="item in list"
            :key="item.id"
            class="template-card"
            :class="{
              'template-card--applied': isAppliedTemplate(item),
              'template-card--default': isDefaultTemplate(item)
            }"
          >
            <div class="template-phone">
              <div class="template-cover">
                <div class="template-cover__status">
                  <span v-if="isAppliedTemplate(item)" class="template-cover__badge template-cover__badge--applied">应用中</span>
                  <span v-if="isDefaultTemplate(item)" class="template-cover__badge template-cover__badge--default">默认</span>
                </div>
                <el-image v-if="shouldShowPreviewImage(item)" :src="item.previewImg" fit="cover" class="template-cover__image" />
                <div v-else class="template-cover__placeholder" :class="getPreviewThemeClass(item)">
                  <div class="template-mock">
                    <div class="template-mock__search"></div>
                    <div class="template-mock__hero">
                      <Icon :icon="getCardIndustryIcon(item.industry)" :size="26" />
                      <span>{{ item.name }}</span>
                    </div>
                    <div class="template-mock__notice">
                      <i></i>
                      <span>{{ getIndustryName(item.industry) }}首页装修模板</span>
                    </div>
                    <div class="template-mock__grid">
                      <i v-for="index in 8" :key="index"></i>
                    </div>
                    <div class="template-mock__list">
                      <i v-for="index in 4" :key="index"></i>
                    </div>
                  </div>
                </div>
                <div class="template-cover__mask">
                  <el-button type="warning" @click="handlePreview(item)">
                    <Icon icon="ep:view" class="mr-5px" />预览
                  </el-button>
                </div>
              </div>
            </div>
            <div class="template-body">
              <div class="template-name" :title="item.name">{{ item.name }}</div>
              <div class="template-meta">
                <el-tag :type="getIndustryTagType(item.industry)" effect="light">
                  <Icon :icon="getCardIndustryIcon(item.industry)" class="mr-4px" />
                  {{ getIndustryName(item.industry) }}
                </el-tag>
                <el-tag :type="getStatusType(item.status)" effect="plain">{{ getStatusLabel(item.status) }}</el-tag>
                <el-tag type="info" effect="plain">{{ getSceneLabel(item.scene) }}</el-tag>
                <el-tag v-if="isAppliedTemplate(item)" type="success" effect="dark">应用中</el-tag>
              </div>
              <div class="template-actions">
                <el-button link type="primary" @click="handleOpenEditor(item)">编辑</el-button>
                <el-button v-if="!isDefaultTemplate(item)" link type="primary" @click="handleOpenDesigner(item)">设计</el-button>
                <el-button link type="primary" @click="handlePreview(item)">预览</el-button>
                <el-button
                  link
                  :type="isDefaultTemplate(item) ? 'warning' : 'success'"
                  :disabled="!canApplyTemplate(item)"
                  :loading="applyLoadingId === item.id"
                  @click="handleApply(item)"
                >
                  {{ getApplyButtonLabel(item) }}
                </el-button>
                <el-button
                  v-if="!isDefaultTemplate(item)"
                  link
                  :type="isPublishedTemplate(item) ? 'warning' : 'success'"
                  :loading="publishLoadingId === item.id"
                  @click="handleTogglePublish(item)"
                >
                  {{ getPublishActionLabel(item) }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </el-skeleton>
    <Pagination v-model:page="queryParams.pageNo" v-model:limit="queryParams.pageSize" :total="total" @pagination="getList" />
  </ContentWrap>

  <TemplateForm ref="formRef" :industries="industryList" :locked-industry="currentIndustry" @success="getList" />

  <el-dialog
    v-model="previewVisible"
    width="980px"
    class="template-preview-dialog"
    destroy-on-close
    append-to-body
    :show-close="true"
  >
    <div v-if="previewItem" class="template-preview">
      <div class="template-preview__phone">
        <div class="template-preview__phone-bar">
          <span>{{ getIndustryName(previewItem.industry) }}</span>
        </div>
        <div class="template-preview__screen">
          <el-image
            v-if="shouldShowPreviewImage(previewItem)"
            :src="previewItem.previewImg"
            fit="contain"
            class="template-preview__image"
          />
          <iframe
            v-else-if="previewUrl"
            class="template-preview__iframe"
            :src="previewUrl"
          ></iframe>
          <div v-else class="template-cover__placeholder" :class="getPreviewThemeClass(previewItem)">
            <div class="template-mock">
              <div class="template-mock__search"></div>
              <div class="template-mock__hero">
                <Icon :icon="getCardIndustryIcon(previewItem.industry)" :size="26" />
                <span>{{ previewItem.name }}</span>
              </div>
              <div class="template-mock__notice">
                <i></i>
                <span>{{ getIndustryName(previewItem.industry) }}首页装修模板</span>
              </div>
              <div class="template-mock__grid">
                <i v-for="index in 8" :key="index"></i>
              </div>
              <div class="template-mock__list">
                <i v-for="index in 4" :key="index"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="template-preview__side">
        <div class="template-preview__illustration">
          <Icon icon="ep:iphone" :size="82" />
          <span>{{ previewItem.name }}</span>
        </div>
        <div class="template-preview__actions">
          <el-button
            type="primary"
            :disabled="!canApplyTemplate(previewItem)"
            :loading="applyLoadingId === previewItem.id"
            @click="handlePreviewApply"
          >
            马上替换
          </el-button>
          <el-button @click="previewVisible = false">关闭</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
// ==================== 依赖引入 ====================
import { ElMessage, ElMessageBox, type FormInstance } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'
import TemplateForm from './TemplateForm.vue'
import * as DiyTemplateApi from '@/api/diy/template'
import {
  CARD_COL_SPAN,
  CARD_GUTTER,
  CARD_SKELETON_COUNT,
  DEFAULT_SCENE_OPTIONS,
  TEMPLATE_STATUS_OPTIONS,
  getIndustryIcon,
  getIndustryTagType,
  getSceneLabel,
  getStatusLabel,
  getStatusType
} from './data'

// ==================== 类型定义 ====================
type TemplateFormExpose = InstanceType<typeof TemplateForm>

// ==================== Props & Emits ====================

// ==================== 响应式数据 ====================
const route = useRoute()
const router = useRouter()
const loading = ref(false) // 列表 loading 状态
const total = ref(0) // 分页总数
const list = ref<DiyTemplateApi.DiyTemplateVO[]>([]) // 模板列表
const industryList = ref<DiyTemplateApi.DiyIndustryVO[]>([]) // 业态列表
const publishLoadingId = ref<number>() // 当前发布中的模板ID
const applyLoadingId = ref<number>()
const queryFormRef = ref<FormInstance>() // 查询表单实例
const formRef = ref<TemplateFormExpose>() // 新建编辑弹窗实例
const previewVisible = ref(false)
const previewItem = ref<DiyTemplateApi.DiyTemplateVO>()
const queryParams = reactive({
  pageNo: 1,
  pageSize: 12,
  name: '',
  industry: undefined as string | undefined,
  status: undefined as number | undefined,
  scene: undefined as string | undefined,
  applied: undefined as boolean | undefined
})

// ==================== 计算属性 ====================
const currentIndustry = computed(() => (route.query.industry as string) || '')
const currentScene = computed(() => (route.query.scene as string) || '')
const industrySelectDisabled = computed(() => !!currentIndustry.value)
const hasAppliedTemplate = computed(() => list.value.some((item) => isAppliedTemplate(item)))
const previewUrl = computed(() => {
  if (!previewItem.value?.id) return ''
  const mobileBaseUrl = import.meta.env.VITE_MOBILE_BASE_URL ||
    (import.meta.env.VITE_DEV === 'true' ? 'http://localhost:3000' : import.meta.env.VITE_MALL_H5_DOMAIN)
  if (!mobileBaseUrl) return ''
  if (isDefaultTemplate(previewItem.value)) {
    return `${mobileBaseUrl}${getNativeHomePath(previewItem.value.industry)}`
  }
  const query = new URLSearchParams({ templateId: String(previewItem.value.id), platform: previewItem.value.platform || 'h5' })
  return `${mobileBaseUrl}/pages/diy/render?${query.toString()}`
})

// ==================== 生命周期 ====================
onMounted(async () => {
  syncIndustryFromRoute()
  await loadIndustryList()
  await getList()
})

watch([currentIndustry, currentScene], async () => {
  syncIndustryFromRoute()
  await getList()
})

// ==================== 方法 ====================
/** 从概况/商城等入口带入的 industry、scene 写入筛选（商城：mall+home；活动：activity+home） */
const syncIndustryFromRoute = () => {
  queryParams.industry = currentIndustry.value || undefined
  queryParams.scene = currentScene.value || undefined
  queryParams.pageNo = 1
}

const loadIndustryList = async () => {
  try {
    const data = await DiyTemplateApi.getIndustryList()
    industryList.value = data || []
  } catch {
    ElMessage.error('业态列表加载失败')
  }
}

const getList = async () => {
  loading.value = true
  try {
    const data = await DiyTemplateApi.getDiyTemplatePage(queryParams)
    list.value = normalizeTemplateList(data.list || [])
    total.value = data.total || 0
  } catch {
    ElMessage.error('模板列表加载失败')
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const handleResetQuery = () => {
  queryFormRef.value?.resetFields()
  syncIndustryFromRoute()
  handleQuery()
}

const handleCreate = () => {
  formRef.value?.open('create', undefined, currentIndustry.value || undefined)
}

const handleOpenEditor = (item: DiyTemplateApi.DiyTemplateVO) => {
  formRef.value?.open('update', item.id)
}

const handleOpenDesigner = (item: DiyTemplateApi.DiyTemplateVO) => {
  if (isDefaultTemplate(item)) {
    ElMessage.warning('默认模板不可设计')
    return
  }
  if (!item.id) return
  const { href } = router.resolve(`/diy/template/editor/${item.id}`)
  window.open(href, '_blank', 'noopener,noreferrer')
}

const handlePreview = (item: DiyTemplateApi.DiyTemplateVO) => {
  previewItem.value = item
  previewVisible.value = true
}

const handleTogglePublish = async (item: DiyTemplateApi.DiyTemplateVO) => {
  if (isDefaultTemplate(item)) {
    ElMessage.warning('默认模板不可下架')
    return
  }
  if (!item.id) {
    // 没有模板ID时无法发布
    return
  }
  publishLoadingId.value = item.id
  try {
    if (isPublishedTemplate(item)) {
      await ElMessageBox.confirm(`确认下架模板「${item.name}」吗？`, '下架确认', { type: 'warning' })
      await DiyTemplateApi.offlineDiyTemplate(item.id)
      ElMessage.success('下架成功')
    } else {
      await DiyTemplateApi.publishDiyTemplate(item.id)
      ElMessage.success('发布成功')
    }
    await getList()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(isPublishedTemplate(item) ? '模板下架失败' : '模板发布失败')
    }
  } finally {
    publishLoadingId.value = undefined
  }
}

const getIndustryName = (industry?: string) => {
  const target = industryList.value.find((item) => item.industry === industry || item.code === industry)
  return target?.name || industry || '未配置业态'
}

const getCardIndustryIcon = (industry?: string) => {
  const target = industryList.value.find((item) => item.industry === industry || item.code === industry)
  return getIndustryIcon(industry, target?.icon)
}

const nativeHomePaths: Record<string, string> = {
  activity: '/pages/modules/activity/activity_list',
  business: '/pages/modules/business/pages/main-tabs/index',
  bosscircle: '/pages/modules/business/pages/main-tabs/index',
  cms: '/pages/modules/cms/pages/article/list',
  event: '/pages/modules/event/pages/index/index',
  food: '/pages/modules/food/home/index',
  hotel: '/pages/modules/hotel/index/index',
  live: '/pages/modules/live/pages/index/index',
  mall: '/pages/modules/mall/index/index',
  microscene: '/pages/modules/microscene/index',
  o2o: '/pages/modules/visiting/index/index',
  'qiling-ai': '/pages/modules/qiling-ai/index',
  rental: '/pages/modules/rental/index/index',
  scenic: '/pages/modules/scenic/home/index',
  shortvideo: '/pages/modules/shortvideo/pages/index/index',
  survey: '/pages/modules/survey/index',
  course: '/pages/modules/course/pages/home/index',
  travel: '/pages/modules/travel/home/index'
}

const getNativeHomePath = (industry?: string) => nativeHomePaths[industry || ''] || '/pages/index/index'

const isDefaultTemplate = (item: DiyTemplateApi.DiyTemplateVO) => {
  return readFlag(item.defaultTemplate) || readFlag(item.isDefault) || Boolean(item.name?.includes('默认'))
}

const isAppliedTemplate = (item: DiyTemplateApi.DiyTemplateVO) => {
  return readFlag(item.applied) || readFlag(item.isApplied)
}

const isPublishedTemplate = (item: DiyTemplateApi.DiyTemplateVO) => {
  return item.status === 1
}

const canApplyTemplate = (item: DiyTemplateApi.DiyTemplateVO) => {
  if (!item.id || isAppliedTemplate(item)) {
    return false
  }
  if (isDefaultTemplate(item)) {
    return hasAppliedCustomTemplateInScope(item)
  }
  return isPublishedTemplate(item)
}

const getApplyButtonLabel = (item: DiyTemplateApi.DiyTemplateVO) => {
  if (isAppliedTemplate(item)) {
    return '应用中'
  }
  if (isDefaultTemplate(item)) {
    return '恢复默认'
  }
  return '应用'
}

const getPublishActionLabel = (item: DiyTemplateApi.DiyTemplateVO) => {
  return isPublishedTemplate(item) ? '下架' : '发布'
}

const getPreviewThemeClass = (item: DiyTemplateApi.DiyTemplateVO) => {
  if (isAppliedTemplate(item)) {
    return 'template-cover__placeholder--applied'
  }
  if (isDefaultTemplate(item)) {
    return 'template-cover__placeholder--default'
  }
  return ''
}

const shouldShowPreviewImage = (item: DiyTemplateApi.DiyTemplateVO) => {
  return Boolean(item.previewImg)
}

const handleApply = async (item: DiyTemplateApi.DiyTemplateVO) => {
  if (!item.id || !canApplyTemplate(item)) return false
  applyLoadingId.value = item.id
  try {
    await DiyTemplateApi.applyDiyTemplate(item.id)
    markTemplateApplied(item)
    ElMessage.success(isDefaultTemplate(item) ? '已恢复默认首页' : '模板已应用')
    await getList()
    markTemplateApplied(item)
    return true
  } catch {
    ElMessage.error('模板应用失败，请确认模板已发布')
    return false
  } finally {
    applyLoadingId.value = undefined
  }
}

const handlePreviewApply = async () => {
  if (!previewItem.value) return
  const applied = await handleApply(previewItem.value)
  if (applied) {
    previewVisible.value = false
  }
}

const markTemplateApplied = (target: DiyTemplateApi.DiyTemplateVO) => {
  list.value = list.value.map((item) => {
    const matchedScope = isSameApplyScope(item, target)
    if (!matchedScope) {
      return item
    }
    const applied = item.id === target.id
    return { ...item, applied, isApplied: applied }
  })
}

const normalizePlatform = (platform?: string) => platform || 'h5'

const normalizeScene = (scene?: string) => scene || 'home'

const isSameApplyScope = (
  source: DiyTemplateApi.DiyTemplateVO,
  target: DiyTemplateApi.DiyTemplateVO
) => {
  return (
    source.industry === target.industry &&
    normalizeScene(source.scene) === normalizeScene(target.scene) &&
    normalizePlatform(source.platform) === normalizePlatform(target.platform)
  )
}

const readFlag = (value: unknown) => {
  return value === true || value === 1 || value === '1' || value === 'true' || value === '\u0001'
}

const hasAppliedCustomTemplateInScope = (target: DiyTemplateApi.DiyTemplateVO) => {
  return list.value.some((item) => {
    return isSameApplyScope(item, target) && !isDefaultTemplate(item) && isAppliedTemplate(item)
  })
}

const normalizeTemplateState = (item: DiyTemplateApi.DiyTemplateVO) => {
  const defaultTemplate = isDefaultTemplate(item)
  const applied = isAppliedTemplate(item)
  return {
    ...item,
    defaultTemplate,
    isDefault: defaultTemplate,
    applied,
    isApplied: applied
  }
}

const normalizeTemplateList = (items: DiyTemplateApi.DiyTemplateVO[]) => {
  const normalizedList = items.map(normalizeTemplateState)
  if (queryParams.applied === false) {
    return normalizedList.map((item) => ({
      ...item,
      applied: false,
      isApplied: false
    }))
  }
  return normalizedList.map((item) => {
    if (!isDefaultTemplate(item)) {
      return item
    }
    const hasAppliedCustomTemplate = normalizedList.some((target) => {
      return isSameApplyScope(target, item) && !isDefaultTemplate(target) && isAppliedTemplate(target)
    })
    if (!hasAppliedCustomTemplate) {
      return item
    }
    return {
      ...item,
      applied: false,
      isApplied: false
    }
  })
}

</script>

<style scoped lang="scss">
.industry-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.template-state-alert {
  margin-bottom: 16px;
}

.template-query-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 0;
}

.template-query-form :deep(.el-form-item) {
  margin-right: 0;
  margin-bottom: 0;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 260px);
  gap: 32px;
  align-items: start;
  justify-content: start;
}

.template-card {
  position: relative;
  width: 260px;
  min-width: 0;
  padding: 0;
  background: transparent;
  border: 0;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);

    .template-cover__mask {
      opacity: 1;
    }
  }
}

.template-card--applied {
  .template-phone {
    border-color: var(--el-color-success);
    box-shadow: 0 10px 26px rgb(82 196 26 / 20%);
  }

  &::after {
    position: absolute;
    top: -9px;
    right: 10px;
    z-index: 4;
    padding: 3px 9px;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    color: #ffffff;
    content: '当前使用';
    background: var(--el-color-success);
    border-radius: 4px;
    box-shadow: 0 4px 10px rgb(82 196 26 / 24%);
  }
}

.template-card--default:not(.template-card--applied) {
  .template-phone {
    border-color: #f1d29a;
  }
}

.template-phone {
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--el-border-color);
  border-radius: 6px;
  box-shadow: 0 2px 10px rgb(0 0 0 / 6%);
}

.template-cover {
  position: relative;
  display: flex;
  aspect-ratio: 375 / 667;
  min-height: 0;
  max-height: none;
  overflow: hidden;
  background: #f7f8fa;
  align-items: center;
  justify-content: center;
}

.template-cover__image {
  width: 100%;
  height: 100%;
  background: #ffffff;
}

.template-cover__image :deep(img) {
  transform: scale(1.08);
}

.template-cover__placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  padding: 10px;
  color: #1f2937;
  background: linear-gradient(180deg, #fdf7ed 0%, #fff8ef 42%, #ffffff 100%);
  align-items: stretch;
  justify-content: stretch;
}

.template-cover__placeholder--default {
  color: #6f4611;
  background: linear-gradient(180deg, #f7f8fa 0%, #ffffff 48%, #f7f8fa 100%);
}

.template-cover__placeholder--applied {
  color: #0f5132;
  background: linear-gradient(180deg, #e8f8ef 0%, #f7fcf9 50%, #ffffff 100%);
}

.template-cover__mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  background: rgb(0 0 0 / 42%);
  opacity: 0;
  transition: opacity 0.2s ease;
  align-items: center;
  justify-content: center;
}

.template-cover__status {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  gap: 6px;
}

.template-cover__badge {
  padding: 2px 8px;
  font-size: 12px;
  line-height: 18px;
  border-radius: 4px;
}

.template-cover__badge--applied {
  color: #ffffff;
  background: var(--el-color-success);
}

.template-cover__badge--default {
  color: #7a4b00;
  background: #ffe7ba;
}

.template-mock {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
}

.template-mock__hero {
  display: flex;
  min-height: 116px;
  padding: 0 12px;
  margin-top: 10px;
  font-size: 16px;
  font-weight: 600;
  color: currentcolor;
  background: linear-gradient(135deg, rgb(255 255 255 / 90%) 0%, rgb(255 255 255 / 56%) 100%);
  border: 1px solid rgb(255 255 255 / 90%);
  border-radius: 8px;
  align-items: center;
  gap: 10px;

  span {
    display: -webkit-box;
    overflow: hidden;
    line-height: 22px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.template-mock__search {
  height: 28px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 4%);
}

.template-mock__notice {
  display: flex;
  height: 28px;
  margin-top: 10px;
  padding: 0 8px;
  font-size: 11px;
  color: #666666;
  background: rgb(255 255 255 / 82%);
  border-radius: 4px;
  align-items: center;
  gap: 6px;

  i {
    width: 12px;
    height: 12px;
    background: currentcolor;
    border-radius: 50%;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.template-mock__grid {
  display: grid;
  margin-top: 12px;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px 8px;

  i {
    aspect-ratio: 1;
    background: rgb(255 255 255 / 90%);
    border-radius: 50%;
  }
}

.template-mock__list {
  display: grid;
  margin-top: 14px;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  i {
    height: 56px;
    background: rgb(255 255 255 / 92%);
    border-radius: 6px;
  }
}

.template-body {
  padding: 10px 2px 0;
}

.template-meta,
.template-actions {
  display: flex;
  align-items: center;
}

.template-meta {
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px;
}

.template-name {
  height: 24px;
  margin-bottom: 8px;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  line-height: 24px;
  color: var(--el-text-color-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-actions {
  min-height: 28px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 4px 8px;
}

.template-skeleton {
  width: 100%;
  height: 260px;
  border-radius: 8px;
}

:deep(.template-preview-dialog) {
  border-radius: 8px;
}

:deep(.template-preview-dialog .el-dialog__body) {
  padding: 10px 28px 30px;
}

.template-preview {
  display: grid;
  min-height: 620px;
  grid-template-columns: 375px 1fr;
  gap: 56px;
  align-items: center;
}

.template-preview__phone {
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--el-border-color);
  border-radius: 6px;
  box-shadow: 0 12px 30px rgb(0 0 0 / 12%);
}

.template-preview__phone-bar {
  display: flex;
  height: 38px;
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  background: #ffffff;
  align-items: center;
  justify-content: center;
}

.template-preview__screen {
  position: relative;
  width: 375px;
  height: 610px;
  overflow-x: hidden;
  overflow-y: auto;
  background: #f7f8fa;
}

.template-preview__image {
  width: 100%;
  min-height: 100%;
}

.template-preview__image :deep(img) {
  display: block;
  width: 100%;
  height: auto;
}

.template-preview__iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.template-preview__side {
  display: flex;
  min-height: 420px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 44px;
}

.template-preview__illustration {
  display: flex;
  width: 300px;
  height: 240px;
  color: #94a3b8;
  background: #f8fafc;
  border: 1px dashed #d7dee8;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 18px;

  span {
    max-width: 240px;
    overflow: hidden;
    font-size: 14px;
    color: #64748b;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.template-preview__actions {
  display: flex;
  gap: 12px;
}
</style>
